import { useTranslation } from 'react-i18next';
import {
  useRef,
  useEffect,
  useContext,
  useReducer,
  useCallback,
  createContext,
  type ReactNode,
} from 'react';

import { Box, Tooltip, IconButton } from '@mui/material';
import {
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  FitScreen as FitScreenIcon,
} from '@mui/icons-material';

// ─── Zoom/pan state ───────────────────────────────────────────────────────────

interface ViewportState {
  zoom: number;
  panX: number;
  panY: number;
}

type ViewportAction =
  | { type: 'SET'; zoom: number; panX: number; panY: number }
  | { type: 'PAN'; dx: number; dy: number }
  | { type: 'ZOOM_AT'; nextZoom: number; focalX: number; focalY: number };

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;

function clampZoom(z: number) {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

function reducer(state: ViewportState, action: ViewportAction): ViewportState {
  switch (action.type) {
    case 'SET':
      return { zoom: clampZoom(action.zoom), panX: action.panX, panY: action.panY };
    case 'PAN':
      return { ...state, panX: state.panX + action.dx, panY: state.panY + action.dy };
    case 'ZOOM_AT': {
      const clamped = clampZoom(action.nextZoom);
      // Zoom toward focal point: keep scene coords under cursor stable
      const sceneX = (action.focalX - state.panX) / state.zoom;
      const sceneY = (action.focalY - state.panY) / state.zoom;
      return {
        zoom: clamped,
        panX: action.focalX - sceneX * clamped,
        panY: action.focalY - sceneY * clamped,
      };
    }
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface ViewportContextValue {
  zoom: number;
  panX: number;
  panY: number;
  fitToView: () => void;
}

const ViewportContext = createContext<ViewportContextValue>({
  zoom: 1,
  panX: 0,
  panY: 0,
  fitToView: () => {},
});

export function useEmailCanvasViewport() {
  return useContext(ViewportContext);
}

// ─── Workspace background CSS (subtle dot grid, Figma-style) ─────────────────

// Exported so a surface without pan/zoom can still sit on the same workspace.
export const CHECKER_BG = {
  backgroundColor: '#F0F0F2',
  backgroundImage: 'radial-gradient(circle, #C8C8D0 1px, transparent 1px)',
  backgroundSize: '20px 20px',
};

// ─── Component ────────────────────────────────────────────────────────────────

export interface ViewportPersistedState {
  zoom: number;
  panX: number;
  panY: number;
}

interface EmailCanvasViewportProps {
  /** Width of the email column in scene (unzoomed) pixels. Used for fit-to-view. */
  emailWidth: number;
  /** Height of the email content in scene pixels — used only for initial centering. */
  emailHeight?: number;
  /**
   * Restored zoom/pan state from a previous mount (e.g. after canvas mode switch).
   * When provided the auto-fit is skipped on first mount.
   */
  initialState?: ViewportPersistedState;
  /** Called whenever zoom or pan changes so callers can persist across remounts. */
  onStateChange?: (state: ViewportPersistedState) => void;
  /** When true, pointer-drag panning is disabled (e.g. while editing a canvas section). */
  disablePan?: boolean;
  /**
   * Fired when the user presses on the empty workspace background (the gray dot
   * grid outside the email frame). Used to clear the current selection — clicks
   * inside the email frame are skipped, and it never fires while panning is
   * disabled (i.e. during canvas editing).
   */
  onBackgroundClick?: () => void;
  children: ReactNode;
}

export function EmailCanvasViewport({
  emailWidth,
  emailHeight = 800,
  initialState,
  onStateChange,
  disablePan = false,
  onBackgroundClick,
  children,
}: EmailCanvasViewportProps) {
  const { t } = useTranslation('emailBuilder');
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, dispatch] = useReducer(reducer, initialState ?? { zoom: 1, panX: 0, panY: 0 });
  const isPanningRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const disablePanRef = useRef(disablePan);
  disablePanRef.current = disablePan;
  const onBackgroundClickRef = useRef(onBackgroundClick);
  onBackgroundClickRef.current = onBackgroundClick;
  // Skip auto-fit if we have a restored state
  const initializedRef = useRef(initialState != null);
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  // Notify caller whenever zoom/pan changes so they can persist across remounts
  useEffect(() => {
    onStateChangeRef.current?.({ zoom: state.zoom, panX: state.panX, panY: state.panY });
  }, [state.zoom, state.panX, state.panY]);

  // ── Fit-to-view ─────────────────────────────────────────────────────────────
  const fitToView = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { width: vw, height: vh } = container.getBoundingClientRect();
    const PAD = 80;
    const scaleX = (vw - PAD * 2) / emailWidth;
    const scaleY = (vh - PAD * 2) / emailHeight;
    const nextZoom = clampZoom(Math.min(scaleX, scaleY, 1));
    const panX = (vw - emailWidth * nextZoom) / 2;
    const panY = PAD;
    dispatch({ type: 'SET', zoom: nextZoom, panX, panY });
  }, [emailWidth, emailHeight]);

  // Initial fit once container is known
  useEffect(() => {
    if (initializedRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    const { width, height } = container.getBoundingClientRect();
    if (width === 0 || height === 0) return;
    initializedRef.current = true;
    const PAD = 80;
    const scaleX = (width - PAD * 2) / emailWidth;
    const nextZoom = clampZoom(Math.min(scaleX, 1));
    const panX = (width - emailWidth * nextZoom) / 2;
    const panY = PAD;
    dispatch({ type: 'SET', zoom: nextZoom, panX, panY });
  });

  // ── Wheel pan (canvas-scoped) ────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Pinch (ctrl+wheel) is handled at the window level so it always zooms
      // the canvas even when the cursor is over the chat panel.
      if (e.ctrlKey) return;
      e.preventDefault();
      dispatch({ type: 'PAN', dx: -e.deltaX, dy: -e.deltaY });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ── Pinch zoom (window-level) ────────────────────────────────────────────────
  // Trackpad pinch arrives as wheel + ctrlKey. Listen on the window so a pinch
  // anywhere on the page zooms the canvas instead of zooming the browser page.
  useEffect(() => {
    const onWindowWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      const el = containerRef.current;
      if (!el) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cursorOverCanvas =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      const focalX = cursorOverCanvas ? e.clientX - rect.left : rect.width / 2;
      const focalY = cursorOverCanvas ? e.clientY - rect.top : rect.height / 2;
      const delta = e.deltaY * -0.01;
      dispatch({
        type: 'ZOOM_AT',
        nextZoom: state.zoom * (1 + delta),
        focalX,
        focalY,
      });
    };
    window.addEventListener('wheel', onWindowWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWindowWheel);
  }, [state.zoom]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        document.activeElement &&
        (document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA' ||
          (document.activeElement as HTMLElement).isContentEditable)
      ) {
        return;
      }
      if (e.key === '0') fitToView();
      if (e.key === '+' || e.key === '=') {
        const c = containerRef.current;
        if (!c) return;
        const { width, height } = c.getBoundingClientRect();
        dispatch({ type: 'ZOOM_AT', nextZoom: state.zoom * 1.2, focalX: width / 2, focalY: height / 2 });
      }
      if (e.key === '-') {
        const c = containerRef.current;
        if (!c) return;
        const { width, height } = c.getBoundingClientRect();
        dispatch({ type: 'ZOOM_AT', nextZoom: state.zoom / 1.2, focalX: width / 2, focalY: height / 2 });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fitToView, state.zoom]);

  // ── Pointer pan ──────────────────────────────────────────────────────────────
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (disablePanRef.current) return;
    // Only pan on the container background (not on sections / interactive children)
    if ((e.target as HTMLElement).closest('[data-email-section]')) return;
    if (e.button !== 0) return;
    // Pressing the empty workspace clears the current selection (Figma-style).
    onBackgroundClickRef.current?.();
    isPanningRef.current = true;
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanningRef.current) return;
    const dx = e.clientX - lastPointerRef.current.x;
    const dy = e.clientY - lastPointerRef.current.y;
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    dispatch({ type: 'PAN', dx, dy });
  }, []);

  const onPointerUp = useCallback(() => {
    isPanningRef.current = false;
  }, []);

  const { zoom, panX, panY } = state;
  const pct = Math.round(zoom * 100);

  return (
    <ViewportContext.Provider value={{ zoom, panX, panY, fitToView }}>
      <Box
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          cursor: disablePan ? 'default' : isPanningRef.current ? 'grabbing' : 'grab',
          userSelect: 'none',
          ...CHECKER_BG,
        }}
      >
        {/* Transformed content layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            transformOrigin: '0 0',
            transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
            // Don't inherit the grab cursor inside the email frame
            cursor: 'default',
            userSelect: 'text',
          }}
        >
          {children}
        </Box>

        {/* Zoom controls — fixed position overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            bgcolor: '#18181B',
            borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.20)',
            px: '4px',
            py: '3px',
            zIndex: 10,
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Tooltip title={t('emailCanvasViewport.zoomOut', 'Zoom out (-)')}>
            <IconButton
              size="small"
              sx={{ p: '4px', color: 'rgba(255,255,255,0.65)', borderRadius: '6px', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' } }}
              onClick={() => {
                const c = containerRef.current;
                if (!c) return;
                const { width, height } = c.getBoundingClientRect();
                dispatch({ type: 'ZOOM_AT', nextZoom: zoom / 1.25, focalX: width / 2, focalY: height / 2 });
              }}
            >
              <ZoomOutIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.8)',
              minWidth: 38,
              textAlign: 'center',
              cursor: 'default',
              letterSpacing: '0.02em',
            }}
          >
            {pct}%
          </Box>

          <Tooltip title={t('emailCanvasViewport.zoomIn', 'Zoom in (+)')}>
            <IconButton
              size="small"
              sx={{ p: '4px', color: 'rgba(255,255,255,0.65)', borderRadius: '6px', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' } }}
              onClick={() => {
                const c = containerRef.current;
                if (!c) return;
                const { width, height } = c.getBoundingClientRect();
                dispatch({ type: 'ZOOM_AT', nextZoom: zoom * 1.25, focalX: width / 2, focalY: height / 2 });
              }}
            >
              <ZoomInIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>

          <Box sx={{ width: '1px', height: 14, bgcolor: 'rgba(255,255,255,0.15)', mx: '2px' }} />

          <Tooltip title={t('emailCanvasViewport.fitToView', 'Fit to view (0)')}>
            <IconButton size="small" sx={{ p: '4px', color: 'rgba(255,255,255,0.65)', borderRadius: '6px', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' } }} onClick={fitToView}>
              <FitScreenIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </ViewportContext.Provider>
  );
}
